import { useState, useRef, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Upload, Image, FileText, Video, Link2, Hash, X, Loader2, Check, AlertCircle,
  Music, File, Plus, Trash2,
} from "lucide-react";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_DOC_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_GALLERY_FILES = 5;

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"];
const ALLOWED_AUDIO_TYPES = ["audio/mpeg", "audio/wav", "audio/ogg", "audio/mp4", "audio/x-m4a", "audio/webm"];
const ALLOWED_DOC_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];

type SubmissionType = "image" | "text" | "video" | "link" | "numeric" | "audio" | "document";

const SUBMISSION_TYPE_CONFIG: Record<SubmissionType, {
  icon: typeof Image;
  label: string;
  description: string;
  acceptsFile: boolean;
  allowedTypes: string[];
  maxSize: number;
  maxSizeLabel: string;
  acceptString: string;
  formatHint: string;
}> = {
  image: {
    icon: Image, label: "Image", description: "Upload a photo or artwork",
    acceptsFile: true, allowedTypes: ALLOWED_IMAGE_TYPES, maxSize: MAX_IMAGE_SIZE,
    maxSizeLabel: "10MB", acceptString: ALLOWED_IMAGE_TYPES.join(","),
    formatHint: "JPG, PNG, GIF, WebP",
  },
  video: {
    icon: Video, label: "Video", description: "Upload a video clip",
    acceptsFile: true, allowedTypes: ALLOWED_VIDEO_TYPES, maxSize: MAX_VIDEO_SIZE,
    maxSizeLabel: "50MB", acceptString: ALLOWED_VIDEO_TYPES.join(","),
    formatHint: "MP4, WebM, MOV, AVI",
  },
  audio: {
    icon: Music, label: "Audio", description: "Upload an audio file",
    acceptsFile: true, allowedTypes: ALLOWED_AUDIO_TYPES, maxSize: MAX_AUDIO_SIZE,
    maxSizeLabel: "10MB", acceptString: ALLOWED_AUDIO_TYPES.join(","),
    formatHint: "MP3, WAV, OGG, M4A",
  },
  document: {
    icon: File, label: "Document", description: "Upload a document or PDF",
    acceptsFile: true, allowedTypes: ALLOWED_DOC_TYPES, maxSize: MAX_DOC_SIZE,
    maxSizeLabel: "10MB", acceptString: ALLOWED_DOC_TYPES.join(","),
    formatHint: "PDF, DOC, DOCX, TXT",
  },
  text: {
    icon: FileText, label: "Text", description: "Write your entry",
    acceptsFile: false, allowedTypes: [], maxSize: 0,
    maxSizeLabel: "", acceptString: "", formatHint: "",
  },
  link: {
    icon: Link2, label: "Link", description: "Submit a URL",
    acceptsFile: false, allowedTypes: [], maxSize: 0,
    maxSizeLabel: "", acceptString: "", formatHint: "",
  },
  numeric: {
    icon: Hash, label: "Number", description: "Submit a numeric value",
    acceptsFile: false, allowedTypes: [], maxSize: 0,
    maxSizeLabel: "", acceptString: "", formatHint: "",
  },
};

interface UploadedFile {
  file: File;
  preview: string | null;
  url?: string;
  progress: number;
  status: "pending" | "uploading" | "done" | "error";
  error?: string;
}

interface SubmissionUploadFormProps {
  gameId: number;
  submissionType: SubmissionType;
  gameName: string;
  galleryMode?: boolean; // Allow multiple files
  onSuccess?: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith("image/")) return Image;
  if (mimeType.startsWith("video/")) return Video;
  if (mimeType.startsWith("audio/")) return Music;
  return File;
}

export default function SubmissionUploadForm({
  gameId,
  submissionType,
  gameName,
  galleryMode = false,
  onSuccess,
}: SubmissionUploadFormProps) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [textContent, setTextContent] = useState("");
  const [caption, setCaption] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = trpc.game.uploadSubmissionFile.useMutation();
  const submitEntry = trpc.game.submit.useMutation();
  const utils = trpc.useUtils();

  const config = SUBMISSION_TYPE_CONFIG[submissionType];
  const maxFiles = galleryMode ? MAX_GALLERY_FILES : 1;

  const validateFile = useCallback((f: File): string | null => {
    if (f.size > config.maxSize) {
      return `File too large. Maximum size is ${config.maxSizeLabel}.`;
    }
    if (config.allowedTypes.length > 0 && !config.allowedTypes.includes(f.type)) {
      return `Invalid file format. Accepted: ${config.formatHint}.`;
    }
    return null;
  }, [config]);

  const handleFiles = useCallback((newFiles: File[]) => {
    setError(null);

    const remaining = maxFiles - files.length;
    if (remaining <= 0) {
      setError(`Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed.`);
      return;
    }

    const filesToAdd = newFiles.slice(0, remaining);
    const uploadedFiles: UploadedFile[] = [];

    for (const f of filesToAdd) {
      const validationError = validateFile(f);
      if (validationError) {
        setError(validationError);
        continue;
      }

      let preview: string | null = null;
      if (f.type.startsWith("image/")) {
        preview = URL.createObjectURL(f);
      } else if (f.type.startsWith("video/")) {
        preview = URL.createObjectURL(f);
      } else if (f.type.startsWith("audio/")) {
        preview = URL.createObjectURL(f);
      }

      uploadedFiles.push({
        file: f,
        preview,
        progress: 0,
        status: "pending",
      });
    }

    if (galleryMode) {
      setFiles(prev => [...prev, ...uploadedFiles]);
    } else {
      // Single file mode — replace
      // Clean up old previews
      files.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
      setFiles(uploadedFiles);
    }
  }, [files, maxFiles, validateFile, galleryMode]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [handleFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) handleFiles(selectedFiles);
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [handleFiles]);

  const removeFile = (index: number) => {
    setFiles(prev => {
      const updated = [...prev];
      const removed = updated.splice(index, 1)[0];
      if (removed.preview) URL.revokeObjectURL(removed.preview);
      return updated;
    });
    setError(null);
  };

  const clearAll = () => {
    files.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setFiles([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadSingleFile = async (uploadedFile: UploadedFile, index: number): Promise<{
    url: string; fileName: string; mimeType: string; fileSize: number;
  }> => {
    // Update status to uploading
    setFiles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], status: "uploading", progress: 10 };
      return updated;
    });

    const reader = new FileReader();
    const base64 = await new Promise<string>((resolve, reject) => {
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(uploadedFile.file);
    });

    // Simulate progress during upload
    setFiles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], progress: 40 };
      return updated;
    });

    const result = await uploadFile.mutateAsync({
      gameId,
      fileName: uploadedFile.file.name,
      fileBase64: base64,
      contentType: uploadedFile.file.type,
    });

    // Mark as done
    setFiles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], status: "done", progress: 100, url: result.url };
      return updated;
    });

    return {
      url: result.url,
      fileName: result.fileName,
      mimeType: result.mimeType,
      fileSize: result.fileSize,
    };
  };

  const handleSubmit = async () => {
    setError(null);
    setUploading(true);
    setOverallProgress(0);

    try {
      let contentUrl = textContent;
      let fileMetadata: { originalFileName?: string; mimeType?: string; fileSize?: number } = {};
      let galleryFiles: Array<{ url: string; fileName: string; mimeType: string; fileSize: number }> | undefined;

      if (config.acceptsFile && files.length > 0) {
        if (galleryMode && files.length > 1) {
          // Gallery mode: upload all files
          const uploaded: Array<{ url: string; fileName: string; mimeType: string; fileSize: number }> = [];
          for (let i = 0; i < files.length; i++) {
            const result = await uploadSingleFile(files[i], i);
            uploaded.push(result);
            setOverallProgress(Math.round(((i + 1) / files.length) * 100));
          }
          contentUrl = uploaded[0].url; // Primary file
          galleryFiles = uploaded;
          fileMetadata = {
            originalFileName: files[0].file.name,
            mimeType: files[0].file.type,
            fileSize: files[0].file.size,
          };
        } else {
          // Single file upload
          setOverallProgress(10);
          const result = await uploadSingleFile(files[0], 0);
          contentUrl = result.url;
          fileMetadata = {
            originalFileName: result.fileName,
            mimeType: result.mimeType,
            fileSize: result.fileSize,
          };
          setOverallProgress(80);
        }
      }

      if (!contentUrl || contentUrl.trim() === "") {
        setError("Please provide content for your submission.");
        setUploading(false);
        return;
      }

      setOverallProgress(90);

      // Submit the entry
      await submitEntry.mutateAsync({
        gameId,
        content: contentUrl,
        submissionType,
        caption: caption || undefined,
        originalFileName: fileMetadata.originalFileName,
        mimeType: fileMetadata.mimeType,
        fileSize: fileMetadata.fileSize,
        galleryFiles,
      });

      setOverallProgress(100);
      toast.success("Submission uploaded! Share your link to get votes.");
      utils.game.getSubmissions.invalidate({ gameId });
      clearAll();
      setTextContent("");
      setCaption("");
      onSuccess?.();
    } catch (err: any) {
      const msg = err?.message || "Failed to submit. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      setOverallProgress(0);
    }
  };

  const renderFilePreview = (uploadedFile: UploadedFile, index: number) => {
    const { file: f, preview, status, progress } = uploadedFile;
    const FileIcon = getFileIcon(f.type);

    return (
      <div key={index} className="relative group rounded-lg border border-border/50 bg-secondary/20 overflow-hidden">
        {/* Preview content */}
        <div className="relative">
          {f.type.startsWith("image/") && preview ? (
            <img src={preview} alt={f.name} className="w-full h-40 object-cover" />
          ) : f.type.startsWith("video/") && preview ? (
            <video src={preview} className="w-full h-40 object-cover" />
          ) : f.type.startsWith("audio/") && preview ? (
            <div className="w-full h-40 flex flex-col items-center justify-center bg-gradient-to-br from-purple-500/10 to-teal/10">
              <Music className="w-10 h-10 text-purple-400 mb-2" />
              <audio src={preview} controls className="w-[90%]" />
            </div>
          ) : (
            <div className="w-full h-40 flex flex-col items-center justify-center bg-gradient-to-br from-blue-500/10 to-teal/10">
              <FileIcon className="w-10 h-10 text-blue-400 mb-2" />
              <p className="text-xs text-muted-foreground px-2 text-center truncate max-w-full">
                {f.name}
              </p>
            </div>
          )}

          {/* Remove button */}
          {!uploading && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-1 right-1 w-6 h-6 p-0 bg-background/80 hover:bg-destructive/80 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => removeFile(index)}
            >
              <X className="w-3 h-3" />
            </Button>
          )}

          {/* Status overlay */}
          {status === "done" && (
            <div className="absolute top-1 left-1">
              <Badge className="bg-neon/90 text-background text-[10px] gap-1 px-1.5 py-0.5">
                <Check className="w-2.5 h-2.5" /> Uploaded
              </Badge>
            </div>
          )}
        </div>

        {/* File info + progress */}
        <div className="p-2 space-y-1">
          <p className="text-xs font-medium truncate">{f.name}</p>
          <p className="text-[10px] text-muted-foreground">{formatFileSize(f.size)}</p>
          {(status === "uploading" || status === "done") && (
            <Progress value={progress} className="h-1" />
          )}
          {status === "error" && (
            <p className="text-[10px] text-destructive">Upload failed</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="glass-panel border-neon/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <config.icon className="w-4 h-4 text-neon" />
          Submit Your Entry
          {galleryMode && (
            <Badge variant="outline" className="text-[10px] ml-auto">
              Gallery Mode — up to {MAX_GALLERY_FILES} files
            </Badge>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {config.description} for <span className="text-neon font-medium">{gameName}</span>
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* File Upload Area */}
        {config.acceptsFile && (
          <>
            {/* Gallery preview grid */}
            {files.length > 0 && (
              <div className={`grid gap-2 ${files.length === 1 ? "grid-cols-1" : "grid-cols-2 sm:grid-cols-3"}`}>
                {files.map((f, i) => renderFilePreview(f, i))}

                {/* Add more button (gallery mode) */}
                {galleryMode && files.length < MAX_GALLERY_FILES && !uploading && (
                  <label className="flex flex-col items-center justify-center h-40 rounded-lg border-2 border-dashed border-border/50 hover:border-neon/30 cursor-pointer transition-colors">
                    <Plus className="w-6 h-6 text-muted-foreground mb-1" />
                    <p className="text-xs text-muted-foreground">Add more</p>
                    <input
                      type="file"
                      className="hidden"
                      accept={config.acceptString}
                      multiple={galleryMode}
                      onChange={handleFileSelect}
                    />
                  </label>
                )}
              </div>
            )}

            {/* Drop zone (when no files) */}
            {files.length === 0 && (
              <div
                className={`relative border-2 border-dashed rounded-xl transition-all duration-200 ${
                  isDragging
                    ? "border-neon bg-neon/5 scale-[1.01]"
                    : "border-border/50 hover:border-neon/30 hover:bg-secondary/30"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <label className="flex flex-col items-center justify-center py-10 cursor-pointer">
                  <div className="w-14 h-14 rounded-full bg-neon/10 flex items-center justify-center mb-3">
                    <Upload className="w-6 h-6 text-neon" />
                  </div>
                  <p className="text-sm font-medium mb-1">
                    {isDragging ? "Drop your file here" : "Drag & drop or click to upload"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {config.formatHint} — Max {config.maxSizeLabel}
                  </p>
                  {galleryMode && (
                    <p className="text-xs text-neon/70 mt-1">
                      Upload up to {MAX_GALLERY_FILES} files
                    </p>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept={config.acceptString}
                    multiple={galleryMode}
                    onChange={handleFileSelect}
                  />
                </label>
              </div>
            )}

            {/* Clear all button */}
            {files.length > 1 && !uploading && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground hover:text-destructive gap-1"
                onClick={clearAll}
              >
                <Trash2 className="w-3 h-3" /> Clear all files
              </Button>
            )}
          </>
        )}

        {/* Text/Link/Numeric Input */}
        {!config.acceptsFile && (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">
              {submissionType === "text" ? "Your Entry" :
               submissionType === "link" ? "URL" :
               "Value"}
            </Label>
            {submissionType === "text" ? (
              <Textarea
                placeholder="Write your entry here..."
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                className="min-h-[120px] bg-secondary/30 border-border/50 focus:border-neon/50"
              />
            ) : (
              <Input
                type={submissionType === "numeric" ? "number" : "url"}
                placeholder={submissionType === "link" ? "https://..." : "Enter a number"}
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                className="bg-secondary/30 border-border/50 focus:border-neon/50"
              />
            )}
          </div>
        )}

        {/* Caption */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider">
            Caption <span className="text-muted-foreground/60">(optional)</span>
          </Label>
          <Input
            placeholder="Add a caption to your submission..."
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="bg-secondary/30 border-border/50 focus:border-neon/50"
            maxLength={280}
          />
          <p className="text-xs text-muted-foreground text-right">{caption.length}/280</p>
        </div>

        {/* Overall Progress Bar */}
        {uploading && overallProgress > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Uploading...</span>
              <span className="text-neon font-mono">{overallProgress}%</span>
            </div>
            <Progress value={overallProgress} className="h-2" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={uploading || (config.acceptsFile ? files.length === 0 : !textContent.trim())}
          className="w-full bg-neon hover:bg-neon/90 text-background font-semibold gap-2"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {overallProgress > 0 ? `Uploading ${overallProgress}%...` : "Preparing..."}
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Submit Entry{files.length > 1 ? ` (${files.length} files)` : ""}
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          After submitting, you'll get a unique share link. Share it to get votes and earn promoter rewards!
        </p>
      </CardContent>
    </Card>
  );
}
